import { useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Disclosure } from '@/components/ui/Disclosure'
import { Drawer } from '@/components/ui/Drawer'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useSession } from '@/features/auth/api/useSession'
import {
  useAllocatePayment,
  useIssuingEntityOptions,
  useRecordCasePayment,
} from '@/features/finance/api/useFinance'
import { useCaseReceivables } from '@/features/finance/api/useReceivables'
import { receivableLabel } from '@/features/finance/allocationLabel'
import { normaliseDecimal } from '@/features/finance/lines'
import { allocationRefusal, paymentRefusalMessage } from '@/features/finance/paymentRefusal'
import { useFormat } from '@/i18n/useFormat'
import { ApiError } from '@/lib/api'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { compareDecimal, decimalDifference, decimalSum } from '@/lib/decimalSum'
import { formatMoneyString } from '@/lib/format'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import { queryKeys } from '@/lib/query'
import type {
  AllocationSplit,
  CaseDetail,
  CasePaymentPayload,
  Payment,
  PaymentMethod,
  Receivable,
} from '@/types/domain'

/* «Платёж клиента» (S-21): a client's payment recorded on its case AND said,
 * in the same act, which of the case's open receivables it pays — issued
 * invoices, the client's issued debit notes, tranches no invoice has replaced
 * yet — each with what it still takes and an amount of its own.
 *
 * Two modes, one table:
 *
 *  · `record` — the payment's fields first, then «Открытые долги»; one
 *    `POST /cases/:id/payments` with the ticked rows as `splits` and a
 *    `clientKey` generated when the sheet opened, sent with every attempt of
 *    that open. Payment and splits are one transaction server-side: a row over
 *    its document refuses the whole request and nothing is left behind.
 *  · `allocate` — an existing INBOUND payment («Разнести»): a read-only
 *    summary above the same table, `POST /payments/:id/allocations`, the
 *    receivables asked in the payment's own currency and date.
 *
 * ── Nothing is allocated by itself (AD-11) ─────────────────────────────────
 *
 * Every row starts empty except the ONE the sheet was opened from, which
 * starts ticked for its whole outstanding — the reader pressed «Внести платёж»
 * on that document, and that press is the decision. «Весь остаток» fills one
 * row on a press, with the least of what the row still takes and what the
 * payment has left; it never runs down the list.
 *
 * ── The counter is the second recorded exception to the no-JS-money rule ───
 *
 * «Разнесено» is the sum of the amounts typed on the ticked rows and «Не
 * разнесено» the received amount less it — there is no served figure for a
 * request not yet made. Both go through `lib/decimalSum.ts` (exact, on scaled
 * BigInts, never `Number()`), and so do the two blocking comparisons: a row
 * above what its document still takes, the rows above the payment. The server
 * judges both ceilings again under row locks and is the backstop: its 409
 * names the document and what it can take, and the sheet words it, outlines
 * that row and refetches the remainders.
 *
 * ── A right-edge drawer, with the form sheets' discard guard ───────────────
 *
 * Locked visual decision 1: a side sheet. `Drawer` is the kit's right-edge
 * panel (the full screen below `sm`, its footer pinned), and it has no dirty
 * guard of its own — so the unsaved-changes question is asked in the footer,
 * `LegParamsDrawer`'s way, from one `requestClose` every dismissal funnels
 * through. Below `sm` each row is a card (CSS only — one input per row, never
 * drawn twice) and the footer keeps one full-width primary. */

/** The server's own spellings, keyed to match `finance.payments.methods.*`. */
const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks.
 *  Repeated rather than shared for the reason `PaymentAllocationSheet` gives:
 *  a component file may export only components. */
function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

/** A plain decimal above zero — `compareDecimal` refuses a sign, so a served
 *  negative remainder (an overpaid row) is not one. */
function positive(value: string | undefined | null): value is string {
  return value !== undefined && value !== null && compareDecimal(value, '0') === 1
}

/** A plain decimal printed at least to the cent, sign kept — `'3700'` reads
 *  `3 700.00`. String padding, not arithmetic. */
function atCents(value: string): string {
  const [whole = '0', fraction = ''] = value.split('.')
  return `${whole}.${fraction.padEnd(2, '0')}`
}

/** Whether a decimal string is anything but zero, read off its digits. */
function nonZero(value: string): boolean {
  return /[1-9]/.test(value)
}

/** Today in UTC — the day the server judges «Получено» against
 *  (`businessDate`, `finance/domain.go`): a received date past it is refused.
 *  `PaymentFormSheet`'s and `CarrierPaymentSheet`'s, repeated for the reason
 *  `isValidAmount` gives. NOT the invoice module's local-date `today`: between
 *  midnight and 05:00 in Ashgabat that one already names tomorrow in UTC, the
 *  picker would offer it and the server would refuse it. */
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** A fresh idempotency key — `CarrierPaymentSheet`'s, for its reason. */
function newClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `key-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** A receivable's key in the draft: kind AND id, because nothing about a
 *  uuid says which table it names. */
function rowKey(row: Pick<Receivable, 'kind' | 'id'>): string {
  return `${row.kind}:${row.id}`
}

interface RowDraft {
  checked: boolean
  amount: string
}

interface ClientPaymentDraft {
  clientKey: string
  receivedOn: string
  amount: string
  currency: string
  method: PaymentMethod
  reference: string
  /** '' while it follows the case's entity — derived at render, never
   *  written, so an untouched sheet stays clean. */
  issuingEntityId: string
  feeAmount: string
  /** '' MEANS "the payment's own". */
  feeCurrency: string
  rows: Record<string, RowDraft>
}

export type ClientPaymentSheetProps = {
  open: boolean
  onClose: () => void
} & (
  | {
      mode: 'record'
      /** The case the money is received on: its id, number, client and
       *  issuing entity, and the currency its rate was agreed in. */
      record: CaseDetail
      /** The row «Внести платёж» was pressed on — ticked for its whole
       *  outstanding, and the payment opens in its currency for that amount. */
      openedFrom?: Receivable
    }
  | {
      mode: 'allocate'
      /** An existing INBOUND payment. */
      payment: Payment
      /** Handed the server's row after the allocation lands (the Finance
       *  page's sheet keeps it). */
      onAllocated?: (payment: Payment) => void
    }
)

export function ClientPaymentSheet(props: ClientPaymentSheetProps) {
  const { open, onClose } = props
  const { t } = useTranslation()
  const format = useFormat()
  const formId = useId()
  const queryClient = useQueryClient()
  const { data: user } = useSession()
  const recording = props.mode === 'record'
  const record = props.mode === 'record' ? props.record : undefined
  const payment = props.mode === 'allocate' ? props.payment : undefined
  const openedFrom = props.mode === 'record' ? props.openedFrom : undefined
  const caseId = record?.id ?? payment?.caseId ?? ''

  const { data: entities } = useIssuingEntityOptions({ enabled: open && recording })
  const recordPayment = useRecordCasePayment()
  const allocate = useAllocatePayment()
  const mutation = recording ? recordPayment : allocate

  /* The refusal THIS open is answering, or null — `CarrierPaymentSheet`'s
     rule: the mutation outlives the sheet, and a refusal left standing would
     greet the next open. Remembered during render, never reset in an effect. */
  const [wasOpen, setWasOpen] = useState(open)
  const [heldOver, setHeldOver] = useState<unknown>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setHeldOver(mutation.error)
      setConfirmingDiscard(false)
    }
  }
  const failure = mutation.error !== null && mutation.error !== heldOver ? mutation.error : null

  const draftKey = open ? `${props.mode}:${caseId}:${payment?.id ?? openedFrom?.id ?? ''}` : null
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<ClientPaymentDraft>(
    draftKey,
    () => {
      if (payment) {
        return {
          clientKey: '',
          receivedOn: payment.receivedOn,
          amount: payment.amount.amount,
          currency: payment.amount.currency,
          method: payment.method,
          reference: payment.reference,
          issuingEntityId: payment.issuingEntityId,
          feeAmount: '',
          feeCurrency: '',
          rows: {},
        }
      }
      /* Opened from a row: the payment in that row's currency, for its whole
         outstanding, and that row ticked for it. Otherwise the currency the
         case's rate was agreed in, and nothing filled. */
      const rateCurrency = record?.money?.rate.currency
      const fromCurrency = openedFrom?.total.currency
      const currency =
        fromCurrency ??
        (rateCurrency !== undefined && (CURRENCIES as readonly string[]).includes(rateCurrency)
          ? rateCurrency
          : 'USD')
      const fromOutstanding = openedFrom?.outstanding.amount
      const whole = positive(fromOutstanding) ? fromOutstanding : ''
      return {
        clientKey: newClientKey(),
        receivedOn: today(),
        amount: whole,
        currency,
        method: 'bank_transfer',
        reference: '',
        issuingEntityId: '',
        feeAmount: '',
        feeCurrency: '',
        rows:
          openedFrom && whole !== '' ? { [rowKey(openedFrom)]: { checked: true, amount: whole } } : {},
      }
    },
  )

  /* The receivables, asked in the payment's currency on its date so each row
     says what it can still take in THAT currency (`outstandingInPayment`).
     A new date or currency is a new read; the last one's rows stay on screen
     meanwhile (`keepPrevious`) rather than the table emptying on every change. */
  const receivables = useCaseReceivables(caseId, {
    payIn: draft.currency,
    on: draft.receivedOn,
    enabled: open,
    keepPrevious: true,
  })
  const served = receivables.data
  /* Until the read for THIS currency and date is in, the rows on screen are the
     last date's or none, and the splits a press would send — or leave out — are
     not the ones the reader ticked against today's figures: the DatePicker
     commits a typed date on blur, so the very press that leaves the box starts
     the new read. A failed read is not waited on: the table says so, and the
     payment stays recordable unallocated, as that sentence promises. */
  const readSettling =
    receivables.isFetching ||
    (!receivables.isError && (receivables.isPending || receivables.isPlaceholderData))
  const openRows = (served?.items ?? []).filter(
    (row) => row.allocatable && positive(row.outstanding.amount),
  )

  /* «Наше юрлицо» defaults to the case's (N-24), derived, and only when this
     reader is offered it. */
  const caseEntityId = record?.entityId ?? ''
  const issuingEntityId =
    draft.issuingEntityId ||
    (caseEntityId !== '' && (entities ?? []).some((entity) => entity.id === caseEntityId)
      ? caseEntityId
      : '')

  /* ── What each row may take, and what is typed on it ──────────────────── */

  /** What the row still takes in the payment's currency: the served figure
   *  when the read was asked in it, else — in the row's own currency — its
   *  outstanding. `undefined` across currencies with no rate, and for a figure
   *  served in another currency (the last read's, held while this one runs). */
  const ceilingOf = (row: Receivable): string | undefined =>
    row.outstandingInPayment?.currency === draft.currency
      ? row.outstandingInPayment.amount
      : row.total.currency === draft.currency
        ? row.outstanding.amount
        : undefined

  const rowDraftOf = (row: Receivable): RowDraft =>
    draft.rows[rowKey(row)] ?? { checked: false, amount: '' }

  const ticked = openRows.filter((row) => rowDraftOf(row).checked)
  const typed = ticked.map((row) => normaliseDecimal(rowDraftOf(row).amount))
  const rowAmountBad = (row: Receivable) => {
    const amount = rowDraftOf(row).amount
    return !isValidAmount(amount) || !positive(normaliseDecimal(amount))
  }
  const anyRowBad = ticked.some(rowAmountBad)
  const allocated = anyRowBad ? null : decimalSum(typed)

  const receivedAmount = recording
    ? isValidAmount(draft.amount)
      ? normaliseDecimal(draft.amount)
      : null
    : (payment?.unallocated.amount ?? null)
  /* What the payment has to spend: the amount being received, or — on an
     existing payment — what is still unallocated on it. */
  const unallocated =
    receivedAmount !== null && allocated !== null
      ? decimalDifference(receivedAmount, allocated)
      : null

  /** What the payment has left for one row, the other ticked rows taken off. */
  const remainingFor = (row: Receivable): string | null => {
    if (receivedAmount === null) return null
    const others = ticked
      .filter((other) => rowKey(other) !== rowKey(row))
      .map((other) => normaliseDecimal(rowDraftOf(other).amount))
    const spent = decimalSum(others)
    return spent === null ? null : decimalDifference(receivedAmount, spent)
  }

  /** «Весь остаток»: the least of what the row takes and what the payment has
   *  left; absent when the payment has nothing left for it. */
  const wholeRemainderOf = (row: Receivable): string | undefined => {
    const ceiling = ceilingOf(row)
    if (!positive(ceiling)) return undefined
    const remaining = remainingFor(row)
    if (remaining === null) return ceiling
    if (!positive(remaining)) return undefined
    return compareDecimal(remaining, ceiling) === -1 ? remaining : ceiling
  }

  const overCeiling = (row: Receivable): boolean => {
    const ceiling = ceilingOf(row)
    if (ceiling === undefined || rowAmountBad(row)) return false
    return compareDecimal(normaliseDecimal(rowDraftOf(row).amount), ceiling) === 1
  }

  const refusal = allocationRefusal(failure, t, served)
  const refusedRow = refusal?.targetId

  /* ── Why the primary is refused, when it is ───────────────────────────── */

  const firstOver = ticked.find(overCeiling)
  const blockedReason = readSettling
    ? t('finance.clientPayment.blocked.reading')
    : anyRowBad
      ? t('finance.clientPayment.blocked.rowAmount')
      : firstOver
        ? t('finance.clientPayment.blocked.rowOver', {
            target: receivableLabel(t, firstOver),
            amount: formatMoneyString(ceilingOf(firstOver) ?? '', draft.currency),
          })
        : unallocated !== null && unallocated.startsWith('-')
          ? recording
            ? t('finance.clientPayment.blocked.totalOver')
            : t('finance.clientPayment.blocked.totalOverUnallocated', {
                amount: formatMoneyString(payment?.unallocated.amount ?? '', draft.currency),
              })
          : !recording && ticked.length === 0
            ? t('finance.clientPayment.blocked.nothingTicked')
            : undefined

  /* ── The payment's own fields (record mode) ───────────────────────────── */

  const feeTyped = draft.feeAmount.trim() !== ''
  const fieldsInvalid =
    recording &&
    (draft.receivedOn === '' ||
      !isValidAmount(draft.amount) ||
      !positive(normaliseDecimal(draft.amount)) ||
      draft.reference.trim() === '' ||
      issuingEntityId === '' ||
      (feeTyped && !isValidAmount(draft.feeAmount)))
  const required = (missing: boolean) =>
    touched && missing ? t('common.form.required') : undefined
  /* The server's word under the field it names (a 422's `errors`), so the
     banner's «поля не приняты» points at something. */
  const servedFields = fieldErrors(failure)
  const servedError = (field: string): string | undefined => {
    const code = servedFields[field]?.[0]
    return code ? t(fieldErrorKey(code)) : undefined
  }

  /* ── The banner: the server's refusal, in the reader's words ──────────── */

  const idempotent = failure instanceof ApiError && failure.code === 'idempotency-conflict'
  const banner = failure
    ? idempotent
      ? t('finance.clientPayment.alreadyRecorded')
      : (refusal?.message ?? paymentRefusalMessage(failure, t, served))
    : touched && fieldsInvalid
      ? t('common.form.fixFields')
      : undefined

  const splits: AllocationSplit[] = ticked.map((row) => ({
    targetKind: row.kind,
    targetId: row.id,
    amount: { amount: normaliseDecimal(rowDraftOf(row).amount), currency: draft.currency },
  }))

  /* A refusal the remainders can explain is answered by reading them again:
     an allocation that landed from somewhere else in the meantime is what moved
     them, and the next attempt must be judged against today's figures. */
  const refetchAfter = (error: unknown) => {
    if (error instanceof ApiError && error.status === 409) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    }
  }

  const submit = () => {
    markTouched()
    if (fieldsInvalid || blockedReason !== undefined || mutation.isPending) return
    if (payment) {
      allocate.mutate(
        { paymentId: payment.id, payload: { splits } },
        {
          onSuccess: (allocatedPayment) => {
            if (props.mode === 'allocate') props.onAllocated?.(allocatedPayment)
            onClose()
          },
          onError: refetchAfter,
        },
      )
      return
    }
    if (!record) return
    const payload: CasePaymentPayload = {
      clientKey: draft.clientKey,
      reference: draft.reference.trim(),
      /* The case's own client, by record. A holder of the generic record key
         is read as sent, so the party is named; the client door (Sales)
         discards it and resolves the same client itself. */
      counterpartyName: record.clientName,
      ...(record.clientId ? { clientId: record.clientId } : {}),
      direction: 'in',
      method: draft.method,
      issuingEntityId,
      amount: { amount: normaliseDecimal(draft.amount), currency: draft.currency },
      ...(feeTyped
        ? {
            fee: {
              amount: normaliseDecimal(draft.feeAmount),
              currency: draft.feeCurrency === '' ? draft.currency : draft.feeCurrency,
            },
          }
        : {}),
      receivedOn: draft.receivedOn,
      ...(splits.length > 0 ? { splits } : {}),
    }
    recordPayment.mutate(
      { caseId: record.id, payload },
      { onSuccess: onClose, onError: refetchAfter },
    )
  }

  const requestClose = () => {
    if (confirmingDiscard) {
      setConfirmingDiscard(false)
      return
    }
    if (dirty && !mutation.isPending) {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const patchRow = (row: Receivable, patch: Partial<RowDraft>) =>
    setDraft((current) => ({
      ...current,
      rows: { ...current.rows, [rowKey(row)]: { ...rowDraftOf(row), ...patch } },
    }))

  /* ── Rendering ────────────────────────────────────────────────────────── */

  const pendingForFinance = recording && !can(user, CAP.financePaymentConfirm)
  const otherCurrencyRows = openRows.some((row) => row.total.currency !== draft.currency)
  const submitLabel = mutation.isPending
    ? t('common.form.saving')
    : !recording
      ? t('finance.allocations.submit')
      : splits.length > 0
        ? t('finance.clientPayment.submitAllocate')
        : t('finance.payments.record')

  const counter = (label: string, value: string | null, tone?: 'attention') => (
    <div className="flex flex-col gap-0.5">
      <dt className="asa-eyebrow">{label}</dt>
      <dd
        className={cn(
          'asa-num text-[14px] font-semibold whitespace-nowrap',
          tone === 'attention' ? 'text-attention' : 'text-text-heading',
        )}
      >
        {value}
      </dd>
    </div>
  )

  const footer = confirmingDiscard ? (
    <>
      <span role="alert" className="text-text-body min-w-0 flex-1 text-[13px] leading-snug">
        {t('common.unsaved.body')}
      </span>
      <Button variant="secondary" autoFocus onClick={() => setConfirmingDiscard(false)}>
        {t('common.unsaved.keep')}
      </Button>
      <Button variant="danger" onClick={onClose}>
        {t('common.unsaved.discard')}
      </Button>
    </>
  ) : (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <dl className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-1">
          {recording
            ? counter(
                t('finance.clientPayment.footer.received'),
                receivedAmount === null ? '—' : formatMoneyString(atCents(receivedAmount), draft.currency),
              )
            : counter(
                t('finance.clientPayment.footer.payment'),
                formatMoneyString(payment?.amount.amount ?? '', draft.currency),
              )}
          {counter(
            t('finance.payments.allocated'),
            allocated === null
              ? '—'
              : formatMoneyString(
                  atCents(
                    recording
                      ? allocated
                      : (decimalSum([payment?.allocated.amount ?? '0', allocated]) ?? allocated),
                  ),
                  draft.currency,
                ),
          )}
          {counter(
            t('finance.payments.unallocated'),
            unallocated === null ? '—' : formatMoneyString(atCents(unallocated), draft.currency),
            unallocated !== null && nonZero(unallocated) ? 'attention' : undefined,
          )}
        </dl>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            className="max-sm:hidden"
            disabled={mutation.isPending}
            onClick={requestClose}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            className="max-sm:w-full"
            disabled={mutation.isPending}
            blockedReason={blockedReason}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
      {/* The reason the primary is refused, printed too: a tooltip is not
          something a phone shows. */}
      {blockedReason !== undefined && (touched || ticked.length > 0) ? (
        <p className="text-negative text-[12px] leading-snug">{blockedReason}</p>
      ) : null}
    </div>
  )

  const renderRow = (row: Receivable) => {
    const rowDraft = rowDraftOf(row)
    const label = receivableLabel(t, row)
    const ceiling = ceilingOf(row)
    const otherCurrency = row.total.currency !== draft.currency
    const whole = wholeRemainderOf(row)
    const invalid =
      refusedRow === row.id ||
      overCeiling(row) ||
      (rowDraft.checked && touched && rowAmountBad(row))
    const caption =
      row.kind === 'tranche'
        ? row.expectedDate === null
          ? t('finance.receivables.trancheUndated')
          : t('finance.receivables.trancheDue', { date: format.date(row.expectedDate) })
        : t(
            row.kind === 'invoice'
              ? 'finance.receivables.invoiceIssued'
              : 'finance.receivables.debitNoteIssued',
            {
              date: format.date(row.documentDate),
              total: formatMoneyString(row.total.amount, row.total.currency),
            },
          )
    const fromHere =
      openedFrom !== undefined && rowKey(openedFrom) === rowKey(row) && rowDraft.checked

    return (
      <li
        key={rowKey(row)}
        className={cn(
          'border-line-hair grid grid-cols-1 gap-2 rounded-[var(--radius-md)] border p-3',
          'sm:grid-cols-[minmax(0,1fr)_auto_7.5rem] sm:items-start sm:gap-x-3 sm:rounded-none',
          'sm:border-0 sm:border-b sm:px-0 sm:py-2.5 sm:last:border-b-0',
        )}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <Checkbox
            label={label}
            hint={fromHere ? `${caption} · ${t('finance.clientPayment.openedFrom')}` : caption}
            checked={rowDraft.checked}
            onCheckedChange={(on) => patchRow(row, on ? { checked: true } : { checked: false, amount: '' })}
          />
          {/* Across currencies: what the row takes in the payment's, at the
              payment date's rate — the server's floored figure. */}
          {otherCurrency && ceiling !== undefined ? (
            <span className="asa-num text-text-muted pl-6 text-[12px] font-light">
              {t('finance.clientPayment.inPayment', {
                amount: formatMoneyString(ceiling, draft.currency),
                date: format.date(draft.receivedOn),
              })}
            </span>
          ) : null}
        </div>

        <span className="asa-num text-text-body text-[12.5px] whitespace-nowrap sm:pt-0.5 sm:text-right sm:text-[13px]">
          {/* The phone's own word for the hidden column heading. */}
          <span aria-hidden="true" className="text-text-muted mr-1 font-light sm:hidden">
            {t('finance.clientPayment.columns.outstanding')}
          </span>
          {formatMoneyString(row.outstanding.amount, row.outstanding.currency)}
        </span>

        <div className="flex flex-col items-end gap-1">
          <Input
            inputSize="sm"
            inputMode="decimal"
            value={rowDraft.amount}
            placeholder="0.00"
            onChange={(event) => {
              const amount = event.target.value
              patchRow(row, { amount, ...(amount.trim() !== '' ? { checked: true } : {}) })
            }}
            aria-label={t('finance.clientPayment.rowAmount', { target: label })}
            aria-invalid={invalid ? true : undefined}
            className="asa-num w-full text-right"
          />
          {whole !== undefined ? (
            <button
              type="button"
              aria-label={t('finance.clientPayment.wholeRemainderFor', { target: label })}
              className="text-text-link hover:text-text-link-hover cursor-pointer text-[12px]"
              onClick={() => patchRow(row, { checked: true, amount: atCents(whole) })}
            >
              {t('finance.clientPayment.wholeRemainder')}
            </button>
          ) : null}
          {overCeiling(row) && ceiling !== undefined ? (
            <span className="text-negative text-right text-[12px] leading-snug">
              {t('finance.clientPayment.blocked.rowOver', {
                target: label,
                amount: formatMoneyString(ceiling, draft.currency),
              })}
            </span>
          ) : null}
        </div>
      </li>
    )
  }

  const debts = receivables.isPending ? (
    <p className="text-text-muted text-[12.5px] leading-snug font-light">
      {t('finance.clientPayment.receivablesLoading')}
    </p>
  ) : receivables.isError ? (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-text-muted text-[12.5px] leading-snug">
        {t('finance.clientPayment.receivablesFailed')}
      </p>
      <Button variant="secondary" size="sm" onClick={() => void receivables.refetch()}>
        {t('common.actions.retry')}
      </Button>
    </div>
  ) : openRows.length === 0 ? (
    <p className="border-line text-text-muted rounded-[var(--radius-md)] border border-dashed px-4 py-5 text-center text-[12.5px] leading-snug font-light">
      {recording
        ? t('finance.clientPayment.nothingOpen')
        : t('finance.clientPayment.nothingOpenAllocate')}
    </p>
  ) : (
    <>
      {served?.rateMissing && otherCurrencyRows ? (
        <p className="bg-surface-sunken text-text-muted rounded-md px-3 py-2 text-[12.5px] leading-snug">
          {t('finance.clientPayment.rateMissing', { date: format.date(draft.receivedOn) })}
        </p>
      ) : null}
      {/* The column heads: plain caps on a hairline — no grey fill. */}
      <div
        aria-hidden
        className="border-line text-text-muted hidden grid-cols-[minmax(0,1fr)_auto_7.5rem] gap-x-3 border-b pb-1.5 text-[10px] font-medium tracking-wide uppercase sm:grid"
      >
        <span className="pl-6">{t('finance.clientPayment.columns.document')}</span>
        <span className="text-right">{t('finance.clientPayment.columns.outstanding')}</span>
        <span className="text-right">
          {otherCurrencyRows
            ? t('finance.clientPayment.columns.applyIn', { currency: draft.currency })
            : t('finance.clientPayment.columns.apply')}
        </span>
      </div>
      <ul aria-label={t('finance.clientPayment.debtsHeading')} className="flex flex-col gap-2 sm:gap-0">
        {openRows.map(renderRow)}
      </ul>
    </>
  )

  return (
    <Drawer
      open={open}
      onClose={requestClose}
      title={
        payment
          ? t('finance.clientPayment.allocateTitle', { reference: payment.reference })
          : t('finance.clientPayment.title')
      }
      size="wide"
      footer={footer}
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4 px-4 pt-3 pb-5"
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          submit()
        }}
      >
        {record ? (
          <p className="text-text-muted -mt-1 text-[12.5px] font-light">
            {record.number}
            {record.clientName ? ` · ${record.clientName}` : ''}
          </p>
        ) : null}

        {/* The payment waits for Finance: said before anything is typed, in
            the neutral tone — it is how the act works, not a warning. */}
        {pendingForFinance ? (
          <p className="bg-surface-sunken text-text-body rounded-md px-3 py-2 text-[12.5px] leading-snug">
            {t('finance.clientPayment.pending')}
          </p>
        ) : null}

        {banner ? (
          <div role="alert">
            <Banner tone="negative" title={banner} />
          </div>
        ) : null}

        {payment ? (
          /* The payment being spent, read-only: allocating never edits it. */
          <dl className="bg-surface-sunken flex flex-wrap gap-x-6 gap-y-2 rounded-[var(--radius-md)] p-3">
            <div className="flex flex-col gap-1">
              <dt className="asa-eyebrow">{t('finance.clientPayment.receivedOn')}</dt>
              <dd className="text-text-body text-[13px]">{format.date(payment.receivedOn)}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="asa-eyebrow">{t('finance.payments.reference')}</dt>
              <dd className="text-text-body font-mono text-[13px]">{payment.reference}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="asa-eyebrow">{t('finance.payments.unallocated')}</dt>
              <dd className="asa-num text-text-heading text-[13px] font-semibold">
                {formatMoneyString(payment.unallocated.amount, payment.unallocated.currency)}
              </dd>
            </div>
          </dl>
        ) : (
          <>
            <div className="grid gap-3.5 sm:grid-cols-2">
              {/* Money cannot land in the future: a date past today (UTC, the
                  server's day) is a typo, and the picker refuses it. */}
              <Field
                label={t('finance.clientPayment.receivedOn')}
                required
                error={required(draft.receivedOn === '') ?? servedError('receivedOn')}
              >
                {(fieldProps) => (
                  <DatePicker
                    value={draft.receivedOn}
                    max={today()}
                    onChange={(receivedOn) => setDraft({ ...draft, receivedOn })}
                    {...fieldProps}
                  />
                )}
              </Field>
              <div className="flex gap-2.5">
                <Field
                  label={t('finance.payments.amount')}
                  required
                  className="min-w-0 flex-1"
                  error={
                    (touched && (!isValidAmount(draft.amount) || !positive(normaliseDecimal(draft.amount)))
                      ? t('common.form.invalidNumber')
                      : undefined) ?? servedError('amount')
                  }
                >
                  {(fieldProps) => (
                    <Input
                      inputMode="decimal"
                      value={draft.amount}
                      onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
                      className="asa-num text-right"
                      {...fieldProps}
                    />
                  )}
                </Field>
                <Field
                  label={t('finance.payments.currency')}
                  className="w-24 shrink-0"
                  error={servedError('currency')}
                >
                  {(fieldProps) => (
                    <Select
                      value={draft.currency}
                      onChange={(currency) => setDraft({ ...draft, currency })}
                      options={toOptions(CURRENCIES, (currency) => currency)}
                      {...fieldProps}
                    />
                  )}
                </Field>
              </div>
              <Field
                label={t('finance.payments.method')}
                required
                error={servedError('method')}
              >
                {(fieldProps) => (
                  <Select
                    value={draft.method}
                    onChange={(method) => setDraft({ ...draft, method })}
                    options={toOptions(METHODS, (method) => t(`finance.payments.methods.${method}`))}
                    {...fieldProps}
                  />
                )}
              </Field>
              <Field
                label={t('finance.payments.reference')}
                required
                error={required(draft.reference.trim() === '') ?? servedError('reference')}
              >
                {(fieldProps) => (
                  <Input
                    value={draft.reference}
                    onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
                    className="font-mono"
                    {...fieldProps}
                  />
                )}
              </Field>
              {/* OURS, not the client's: which of the company's entities the
                  money reached, defaulted from the case. */}
              <Field
                label={t('finance.payments.entity')}
                required
                hint={t('finance.clientPayment.entityHint')}
                error={required(issuingEntityId === '') ?? servedError('issuingEntityId')}
                className="sm:col-span-2"
              >
                {(fieldProps) => (
                  <Select
                    value={issuingEntityId}
                    onChange={(picked) => setDraft({ ...draft, issuingEntityId: picked })}
                    options={(entities ?? []).map((entity) => ({
                      value: entity.id,
                      label: entity.name,
                    }))}
                    placeholder={t('common.form.selectPlaceholder')}
                    {...fieldProps}
                  />
                )}
              </Field>
            </div>

            {/* The bank's fee, optional and never added to the amount (0058). */}
            <Disclosure summary={t('finance.clientPayment.feeSummary')} count={2} defaultOpen={feeTyped}>
              <Field
                label={t('finance.payments.fee')}
                hint={t('finance.payments.feeHint')}
                error={
                  (touched && feeTyped && !isValidAmount(draft.feeAmount)
                    ? t('common.form.invalidNumber')
                    : undefined) ?? servedError('feeAmount')
                }
              >
                {(fieldProps) => (
                  <Input
                    inputMode="decimal"
                    value={draft.feeAmount}
                    onChange={(event) => setDraft({ ...draft, feeAmount: event.target.value })}
                    className="asa-num text-right"
                    {...fieldProps}
                  />
                )}
              </Field>
              <Field label={t('finance.payments.feeCurrency')} error={servedError('feeCurrency')}>
                {(fieldProps) => (
                  <Select
                    value={draft.feeCurrency === '' ? draft.currency : draft.feeCurrency}
                    onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                    options={toOptions(CURRENCIES, (currency) => currency)}
                    {...fieldProps}
                  />
                )}
              </Field>
            </Disclosure>
          </>
        )}

        <section className="flex flex-col gap-2">
          <h3 className="asa-eyebrow">{t('finance.clientPayment.debtsHeading')}</h3>
          {debts}
        </section>
      </form>
    </Drawer>
  )
}
